/**
 * @fileoverview PDFビューアダイアログのContext
 * @description モバイル向けPDFビューアの状態管理
 * @module apps/web/components/pdf/pdf-viewer-context
 * @remarks
 * source-card.tsx から PdfViewerDialog の開閉を制御するために使用。
 * chat.tsx などの親コンポーネントで Provider を配置する。
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import type { PdfDialogState } from './pdf-viewer-dialog'

// ============================================================================
// 型定義
// ============================================================================

/**
 * PDFビューアContextの値の型
 */
interface PdfViewerContextValue {
  /** ダイアログの状態 */
  state: PdfDialogState
  /** PDFビューアダイアログを開く */
  openPdfViewer: (pdfUrl: string, page: number) => void
  /** PDFビューアダイアログを閉じる */
  closePdfViewer: () => void
}

// ============================================================================
// Context
// ============================================================================

/**
 * PDFビューアContext
 */
const PdfViewerContext = createContext<PdfViewerContextValue | null>(null)

// ============================================================================
// Provider
// ============================================================================

/**
 * PDFビューアProviderのprops型
 */
interface PdfViewerProviderProps {
  children: ReactNode
}

/**
 * PDFビューアProvider
 * @description PDFビューアダイアログの状態を管理
 * @example
 * ```tsx
 * <PdfViewerProvider>
 *   <ChatPage />
 *   <PdfViewerDialog {...} />
 * </PdfViewerProvider>
 * ```
 */
export function PdfViewerProvider({ children }: PdfViewerProviderProps) {
  const [state, setState] = useState<PdfDialogState>({
    isOpen: false,
    pdfUrl: '',
    page: 1,
  })

  /**
   * PDFビューアダイアログを開く
   */
  const openPdfViewer = useCallback((pdfUrl: string, page: number) => {
    setState({
      isOpen: true,
      pdfUrl,
      page,
    })
  }, [])

  /**
   * PDFビューアダイアログを閉じる
   */
  const closePdfViewer = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
    }))
  }, [])

  return (
    <PdfViewerContext.Provider value={{ state, openPdfViewer, closePdfViewer }}>
      {children}
    </PdfViewerContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

/**
 * PDFビューアContextを使用するhook
 * @throws Provider外で使用した場合にエラー
 * @example
 * ```tsx
 * const { openPdfViewer } = usePdfViewer()
 * openPdfViewer('https://example.com/file.pdf', 54)
 * ```
 */
export function usePdfViewer(): PdfViewerContextValue {
  const context = useContext(PdfViewerContext)
  if (!context) {
    throw new Error('usePdfViewer must be used within a PdfViewerProvider')
  }
  return context
}

// ============================================================================
// ユーティリティ
// ============================================================================

/**
 * モバイルデバイスかどうかを判定
 * @returns true: モバイル（Android, iPhone, iPad）
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }
  return /Android|iPhone|iPad/i.test(navigator.userAgent)
}
